import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper,
  List,
  ListItem,
  Divider,
} from '@mui/material';
import {
  Star as StarIcon,
  Event as EventIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';

interface FormattedAIResponseProps {
  text: string;
  variant?: 'default' | 'compact';
}

const FormattedAIResponse: React.FC<FormattedAIResponseProps> = ({ 
  text, 
  variant = 'default' 
}) => {
  // Parse the AI response text and format it
  const formatAIText = (rawText: string) => {
    // Split text into sections
    const sections = rawText.split(/\d+\.\s\*\*/).filter(Boolean);
    const intro = sections[0];
    const events = sections.slice(1);

    return { intro, events };
  };

  const { intro, events } = formatAIText(text);

  // Extract emojis and clean intro text
  const cleanIntro = intro
    .replace(/🎉|✨|🌟|🎊|🎈/g, '') // Remove emojis
    .replace(/\*\*/g, '') // Remove markdown bold
    .trim();

  // Parse individual event
  const parseEvent = (eventText: string) => {
    const lines = eventText.split('\n').filter(line => line.trim());
    const titleMatch = eventText.match(/^([^-]+)/);
    const title = titleMatch ? titleMatch[1].replace(/\*\*/g, '').trim() : '';
    
    // Extract date info
    const dateMatch = eventText.match(/(\w+ \d+, \d+)/);
    const date = dateMatch ? dateMatch[1] : '';
    
    // Extract description (everything after the first line)
    const description = lines.slice(1).join(' ')
      .replace(/\*\*/g, '') // Remove markdown bold
      .replace(/Imagine.*?behold!/g, '') // Remove flowery language
      .trim();

    return { title, date, description };
  };

  if (variant === 'compact') {
    return (
      <Typography variant="body2" sx={{ 
        color: 'text.secondary',
        fontStyle: 'italic',
        lineHeight: 1.4,
      }}>
        {cleanIntro}
      </Typography>
    );
  }

  return (
    <Box>
      {/* Intro Section */}
      <Paper sx={{ 
        p: 2, 
        mb: 2, 
        bgcolor: 'primary.light', 
        color: 'primary.contrastText',
        borderRadius: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <StarIcon sx={{ fontSize: 20 }} />
          <Typography variant="subtitle1" fontWeight="bold">
            AI Assistant
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
          {cleanIntro}
        </Typography>
      </Paper>

      {/* Events List */}
      {events.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventIcon />
            Found Events
          </Typography>
          
          <List sx={{ p: 0 }}>
            {events.map((eventText, index) => {
              const event = parseEvent(eventText);
              
              return (
                <React.Fragment key={index}>
                  <ListItem sx={{ 
                    flexDirection: 'column', 
                    alignItems: 'flex-start',
                    bgcolor: 'grey.50',
                    borderRadius: 1,
                    mb: 1,
                    p: 2,
                  }}>
                    <Box sx={{ width: '100%', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        {event.title}
                      </Typography>
                    </Box>
                    
                    {event.date && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Chip 
                          label={event.date} 
                          size="small" 
                          variant="outlined"
                          color="primary"
                        />
                      </Box>
                    )}
                    
                    {event.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                        {event.description}
                      </Typography>
                    )}
                  </ListItem>
                  
                  {index < events.length - 1 && <Divider sx={{ my: 1 }} />}
                </React.Fragment>
              );
            })}
          </List>
        </Box>
      )}
    </Box>
  );
};

export default FormattedAIResponse;
